// Administración · Resumen (plan 2026-10-30 §2.1, T-cdqn; handoff README
// §1, prototipo/sec-base.jsx SecResumen): la portada del módulo, en
// /administracion.
//
// Grid 2fr 1fr. A la izquierda «Puesta a punto»: una fila por cosa que la
// cuenta necesita para estar al día —Datos y logo · Equipo invitado ·
// {Tiendas y zonas} · Checklists asignados—, con tick verde o pill
// «Revisar», y cada fila lleva a la sección que lo resuelve. El estado y
// el resumen numérico los calcula core-api (GET /organizations/me/
// setup-status); la fila de equipo se arma aquí con el roster y las
// invitaciones pendientes de auth-svc (features/administracion/resumen/
// model.ts). A la derecha «Últimos
// cambios»: los tres más recientes del registro de configuración y
// «Ver registro» hacia la sección completa.
//
// Lo que el handoff dibuja y esta rama no tiene no se muestra: la fila
// «Contexto de la IA» (la capa no existe aquí) y la tarjeta «Aún con el
// equipo BitácoraPro» con «Proponer cambio» (no hay endpoint de
// solicitudes; sin el botón su copy invita a algo que no se puede hacer).
//
// La guarda de acceso (canAdministerOrg) la pone la ruta en App.tsx; esta
// pantalla asume que quien la ve puede administrar.

import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { OrgMember, SetupStatusItem } from "@bitacorapro/api-types";
import { EmptyState } from "../../components/EmptyState";
import { Icon } from "../../components/icons";
import { PageError } from "../../components/PageError";
import { PageHeader } from "../../components/PageHeader";
import { PageSkeleton } from "../../components/PageSkeleton";
import { Panel } from "../../components/Panel";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Skel } from "../../components/Skel";
import {
  filasPuestaAPunto,
  fmtCuandoCorto,
  quienLabel,
  rosterPorCorreo,
} from "../../features/administracion/resumen/model";
import {
  useInvitacionesPendientes,
  useSetupStatus,
  useUltimosCambios,
} from "../../features/administracion/resumen/queries";
import { adminWords } from "../../lib/admin/words";
import { useRecipe, useRoster } from "../../lib/api/queries";
import { useQueryView } from "../../lib/loading/useQueryView";
import { adminSectionPath } from "../../lib/nav";
import { gateOf, RefreshNotices, SlowNotice } from "../queryViewKit";

const NO_ITEMS: SetupStatusItem[] = [];
const NO_MEMBERS: OrgMember[] = [];

export function Resumen() {
  const recipeQuery = useRecipe();
  const setupQuery = useSetupStatus();
  const rosterQuery = useRoster();
  const invitacionesQuery = useInvitacionesPendientes();
  const cambiosQuery = useUltimosCambios();
  const navigate = useNavigate();

  const recipe = recipeQuery.data;
  const words = useMemo(() => adminWords(recipe), [recipe]);
  const assetManyLower = (recipe?.labels.assetPlural ?? "Activos").toLocaleLowerCase("es-CL");

  // La puesta a punto necesita las cuatro (las palabras, el estado del
  // servidor, y el roster más las invitaciones pendientes para la fila de
  // equipo), así que se pliegan en un solo gate: el primer error gana,
  // luego el skeleton, y la cabecera pinta desde el primer render
  // (ADR-0166). El timeline tiene su propia sección: que falle el registro
  // no esconde la puesta a punto.
  const gate = gateOf([
    useQueryView(recipeQuery),
    useQueryView(setupQuery),
    useQueryView(rosterQuery),
    useQueryView(invitacionesQuery),
  ]);

  function retryAll() {
    for (const query of [recipeQuery, setupQuery, rosterQuery, invitacionesQuery]) {
      if (query.isError || query.isPending) void query.refetch();
    }
  }

  const ready = gate.kind === "ready";
  const items = ready ? gate.data[1].items : NO_ITEMS;
  const members = ready ? gate.data[2].members : NO_MEMBERS;
  const invitacionesPendientes = ready ? gate.data[3].invitations.length : 0;

  const filas = useMemo(
    () => filasPuestaAPunto({ items, members, invitacionesPendientes, words }),
    [items, members, invitacionesPendientes, words]
  );
  const rosterByEmail = useMemo(() => rosterPorCorreo(members), [members]);

  function renderBody() {
    if (gate.kind === "error") {
      return gate.offline ? (
        <PageError
          title="Sin conexión"
          description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
          onRetry={gate.retry}
        />
      ) : (
        <PageError
          title="No pudimos cargar el resumen"
          description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
          onRetry={gate.retry}
        />
      );
    }
    if (gate.kind !== "ready") {
      return (
        <>
          <PageSkeleton
            label="Cargando el resumen…"
            visible={gate.kind === "loading" && gate.visible}
          >
            <ResumenSkeleton />
          </PageSkeleton>
          {gate.kind === "loading" && gate.slow && <SlowNotice onRetry={retryAll} />}
        </>
      );
    }
    return (
      <>
        <RefreshNotices
          refreshFailed={gate.refreshFailed}
          revalidatingHint={gate.revalidatingHint}
          onRetry={retryAll}
        />
        <div className="grid-23">
          <Panel title="Puesta a punto" sub="Lo que falta revisar" flush>
            <ul className="ag-readiness">
              {filas.map((f) => (
                <li key={f.key}>
                  <button
                    type="button"
                    className="ag-readiness__row"
                    title={f.title}
                    onClick={() => navigate(f.to)}
                  >
                    <span className={f.ok ? "ag-tick ag-tick--ok" : "ag-tick"}>
                      {f.ok && <Icon.Check s={12} />}
                    </span>
                    <span className="ag-readiness__main">
                      <span className="ag-readiness__t">{f.titulo}</span>
                      <span className="ag-readiness__s">{f.summary}</span>
                    </span>
                    {!f.ok && <span className="st-pill st-pill--due-overdue">Revisar</span>}
                    <Icon.ChevronRight />
                  </button>
                </li>
              ))}
            </ul>
          </Panel>
          <div className="stack-gap">
            <Panel
              title="Últimos cambios"
              actions={
                <Link
                  className="btn btn--ghost btn--sm"
                  to={adminSectionPath("registro")}
                  title={`Abre ${words.sections.registro} con todos los cambios de configuración`}
                >
                  Ver registro
                </Link>
              }
            >
              <QueryBoundary
                query={cambiosQuery}
                label="los últimos cambios"
                kind="section"
                skeleton={<TimelineSkeleton />}
                isEmpty={(data) => data.items.length === 0}
                empty={
                  <EmptyState
                    title="Sin cambios todavía"
                    description="Lo que tu equipo cambie en Administración aparece aquí."
                  />
                }
              >
                {(data) => (
                  <div className="timeline">
                    {data.items.map((c) => (
                      <div key={c.id} className="timeline__item">
                        <span className="timeline__dot" />
                        <div className="timeline__date">
                          <span className="mono">{fmtCuandoCorto(c.createdAt)}</span>
                        </div>
                        <div className="timeline__title">{c.summary}</div>
                        <div className="ag-muted">
                          {c.actor.kind === "support" ? (
                            <span className="role-pill role-pill--admin">BitácoraPro</span>
                          ) : (
                            quienLabel(c.actor, rosterByEmail)
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </QueryBoundary>
            </Panel>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow={words.module}
        title={words.sections.resumen}
        sub={
          recipeQuery.isPending ? (
            <Skel variant="text" width="55%" />
          ) : recipeQuery.isError ? undefined : (
            `Todo lo que necesitas para mantener tu cuenta al día sin escribirnos: equipo, ${assetManyLower} y checklists.`
          )
        }
      />
      {renderBody()}
    </div>
  );
}

// El mismo grid que los datos, para que el cuerpo no salte al cargar.
function ResumenSkeleton() {
  return (
    <div className="grid-23">
      <Panel flush>
        <ul className="ag-readiness">
          {[0, 1, 2, 3].map((i) => (
            <li key={i}>
              <div className="ag-readiness__row">
                <Skel variant="circle" width={20} height={20} />
                <span className="ag-readiness__main">
                  <Skel variant="text" width="40%" />
                  <Skel variant="text" width="60%" />
                </span>
              </div>
            </li>
          ))}
        </ul>
      </Panel>
      <div className="stack-gap">
        <Panel>
          <TimelineSkeleton />
        </Panel>
      </div>
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="ag-sm">
      <Skel variant="text" width="30%" />
      <Skel variant="text" width="80%" />
      <Skel variant="text" width="30%" />
      <Skel variant="text" width="70%" />
    </div>
  );
}

export default Resumen;
